import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { MdCloudUpload, MdInsertDriveFile } from 'react-icons/md'

interface BlotFileUploadProps {
  onFileUploaded: (file: File) => void
  disabled?: boolean
}

const FORMATS = ['PDF', 'PNG', 'JPG', 'TIFF']

export default function BlotFileUpload({ onFileUploaded, disabled }: BlotFileUploadProps) {
  const { t } = useTranslation()
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        setUploadedFile(file)
        onFileUploaded(file)
      }
    },
    [onFileUploaded]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled,
  })

  const idleBorder = useColorModeValue('gray.200', 'gray.600')

  return (
    <VStack spacing={4} align="stretch">
      <Box
        {...getRootProps()}
        position="relative"
        px={6}
        py={10}
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragActive ? 'purple.400' : idleBorder}
        borderRadius="2xl"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        opacity={disabled ? 0.6 : 1}
        transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
        overflow="hidden"
        bgGradient={
          isDragActive
            ? 'linear(135deg, purple.50, cyan.50)'
            : 'linear(180deg, gray.50, white)'
        }
        boxShadow={isDragActive ? '0 14px 34px -14px rgba(168,85,247,0.4)' : 'none'}
        _hover={
          disabled
            ? {}
            : {
                borderColor: 'purple.300',
                bgGradient: 'linear(135deg, purple.50, cyan.50)',
                transform: 'translateY(-2px)',
                boxShadow: '0 14px 30px -16px rgba(168,85,247,0.32)',
              }
        }
      >
        <input {...getInputProps()} />

        {uploadedFile ? (
          <VStack spacing={3}>
            <Box
              w="60px"
              h="60px"
              borderRadius="2xl"
              bgGradient="linear(135deg, teal.400, cyan.500)"
              display="grid"
              sx={{ placeItems: 'center' }}
              boxShadow="0 10px 22px -8px rgba(20,184,166,0.5)"
            >
              <Icon as={MdInsertDriveFile} boxSize={7} color="white" />
            </Box>
            <VStack spacing={0.5}>
              <Text fontWeight="700" color="gray.800" noOfLines={1} maxW="360px">
                {uploadedFile.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
              </Text>
            </VStack>
          </VStack>
        ) : (
          <VStack spacing={4}>
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bgGradient={
                isDragActive
                  ? 'linear(135deg, purple.500, cyan.500)'
                  : 'linear(135deg, purple.400, cyan.400)'
              }
              display="grid"
              sx={{ placeItems: 'center' }}
              boxShadow="0 12px 26px -8px rgba(168,85,247,0.45)"
              transition="transform 0.25s ease"
              transform={isDragActive ? 'scale(1.08)' : 'scale(1)'}
            >
              <Icon as={MdCloudUpload} boxSize={8} color="white" />
            </Box>

            <VStack spacing={1}>
              <Text fontWeight="700" fontSize="md" color="gray.800" textAlign="center">
                {isDragActive
                  ? t('blotAnalysis.workspace.uploadDropHere')
                  : t('blotAnalysis.workspace.uploadDragDrop')}
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                {t('blotAnalysis.workspace.uploadOrClick')}
              </Text>
            </VStack>

            <VStack spacing={1.5}>
              <HStack spacing={1.5} flexWrap="wrap" justify="center">
                {FORMATS.map((f) => (
                  <Badge
                    key={f}
                    fontSize="2xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                    colorScheme="purple"
                    variant="solid"
                  >
                    {f}
                  </Badge>
                ))}
              </HStack>
              <Text fontSize="2xs" color="gray.400">
                {t('blotAnalysis.workspace.uploadSupported')}
              </Text>
            </VStack>
          </VStack>
        )}
      </Box>

      {uploadedFile && (
        <Button
          size="sm"
          variant="ghost"
          colorScheme="purple"
          onClick={() => setUploadedFile(null)}
          isDisabled={disabled}
          alignSelf="center"
        >
          {t('blotAnalysis.workspace.uploadChooseDifferent')}
        </Button>
      )}
    </VStack>
  )
}
